import { create } from 'zustand';
import { apiClient } from '../lib/api/client';

// ═══════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════

export interface FeatureUiConfig {
    icon?: string;
    upgradeTitle: string;
    upgradeDescription: string;
}

export interface FeatureMeta {
    value: any;
    valueType: 'INTEGER' | 'BOOLEAN' | 'STRING_ARRAY';
    uiConfig: FeatureUiConfig | null;
    name: string;
    description: string | null;
}

export interface PlanFeaturesData {
    planId: string;
    planName: string;
    features: Record<string, FeatureMeta>;
}

export interface PlanFeaturesState {
    // Data
    data: PlanFeaturesData | null;
    isLoading: boolean;
    error: string | null;
    lastFetchedAt: number | null;

    // Actions
    fetchFeatures: () => Promise<void>;
    invalidate: () => void;

    // Selectors
    getFeatureValue: (key: string) => any;
    getFeatureMeta: (key: string) => FeatureMeta | null;
    isWithinLimit: (key: string, currentUsage: number) => boolean;
    getPlanName: () => string;
    getPlanId: () => string;
}

// ═══════════════════════════════════════════════════
// Hardcoded free-tier defaults (frontend fallback)
// Used ONLY before the first API response arrives.
// Once API data is loaded, all values come from the backend
// (which reads admin-configured FeatureDefinition + ProductFeature).
// ═══════════════════════════════════════════════════
const FREE_TIER_DEFAULTS: Record<string, any> = {
    max_pages_per_board: 2,
    max_charts_per_page: 4,
    max_time_range_days: 90,
    max_journals: 1,
    max_operations_per_journal: 20,
    ai_weekly_tokens_limit: 100000,
    max_channels_per_board: 2,
};

const CACHE_TTL_MS = 30 * 1000; // 30 seconds (reduced from 5m to catch admin changes faster)

// ═══════════════════════════════════════════════════
// Store
// ═══════════════════════════════════════════════════

export const usePlanFeaturesStore = create<PlanFeaturesState>()((set, get) => ({
    data: null,
    isLoading: false,
    error: null,
    lastFetchedAt: null,

    fetchFeatures: async () => {
        const state = get();

        // Skip if already loading
        if (state.isLoading) return;

        // Use cache if still fresh
        if (state.data && state.lastFetchedAt && (Date.now() - state.lastFetchedAt) < CACHE_TTL_MS) {
            return;
        }

        set({ isLoading: true, error: null });

        try {
            const response = await apiClient.get<{ success: boolean; data: PlanFeaturesData }>('/subscriptions/features');
            if (response.data.success) {
                set({
                    data: response.data.data,
                    isLoading: false,
                    lastFetchedAt: Date.now(),
                });
            } else {
                set({ isLoading: false, error: 'Failed to fetch plan features' });
            }
        } catch (error: any) {
            console.error('[PlanFeaturesStore] Error fetching features:', error?.message);
            set({ isLoading: false, error: error?.message || 'Unknown error' });
        }
    },

    invalidate: () => {
        set({ lastFetchedAt: null });
        // Immediately re-fetch
        get().fetchFeatures();
    },

    getFeatureValue: (key: string) => {
        // Override if restrictions are explicitly removed
        if (process.env.REMOVE_RESTRICTIONS === 'true') {
            const devValue = FREE_TIER_DEFAULTS[key];
            if (typeof devValue === 'number' || key.startsWith('max_')) return -1;
            if (typeof devValue === 'boolean' || key.startsWith('can_') || key.startsWith('enable_')) return true;
        }

        const data = get().data;
        if (data?.features?.[key]) {
            return data.features[key].value;
        }
        // Hardcoded fallback only when API data hasn't loaded yet
        if (!data) {
            return FREE_TIER_DEFAULTS[key] ?? null;
        }
        // API loaded but key not present — no limit
        return null;
    },

    getFeatureMeta: (key: string) => {
        const data = get().data;
        return data?.features?.[key] ?? null;
    },

    isWithinLimit: (key: string, currentUsage: number) => {
        const limit = get().getFeatureValue(key);
        if (limit === null || limit === undefined) return true;
        if (typeof limit === 'boolean') return limit;
        if (typeof limit === 'number') {
            if (limit === -1) return true; // Unlimited
            return currentUsage < limit;
        }
        return true;
    },

    getPlanName: () => {
        return get().data?.planName || 'free';
    },

    getPlanId: () => {
        return get().data?.planId || 'free';
    },
}));

// ═══════════════════════════════════════════════════
// Convenience hooks
// ═══════════════════════════════════════════════════

/**
 * Hook to get a single feature limit value.
 * Returns the limit value (number, boolean, or array).
 * 
 * @example
 * const maxPages = useFeatureLimit('max_pages_per_board'); // e.g. 2
 * const canExport = useFeatureLimit('can_export_data');     // e.g. false
 */
export const useFeatureLimit = (key: string) => {
    return usePlanFeaturesStore(state => state.getFeatureValue(key));
};

/**
 * Hook to check if a feature usage is within the plan limit.
 * 
 * @example
 * const withinLimit = useIsWithinLimit('max_pages_per_board', pages.length);
 * if (!withinLimit) showUpgradeModal();
 */
export const useIsWithinLimit = (key: string, currentUsage: number) => {
    return usePlanFeaturesStore(state => state.isWithinLimit(key, currentUsage));
};
